import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageCircle, Phone } from 'lucide-react';
import { BUSINESS } from '../../config/business';
import { trackClickPhone, trackClickWhatsapp } from '../../utils/analytics';
import { useKeyboardOpen } from './useKeyboardOpen';

const CONTACT_PATH = '/contact';
/** Hauteur réelle de la barre (safe-area incluse), lue par MainLayout pour son padding-bottom. */
const OFFSET_VAR = '--sticky-bar-offset';

const BUTTON_CLASS =
  'inline-flex h-12 flex-1 items-center justify-center gap-2 rounded-full text-sm font-bold transition-colors';

/** Barre de contact fixe (mobile uniquement) : appel et WhatsApp en un tap. */
const StickyContactBar: React.FC = () => {
  const { pathname } = useLocation();
  const keyboardOpen = useKeyboardOpen(pathname.startsWith(CONTACT_PATH));
  const barRef = useRef<HTMLDivElement>(null);

  // Barre masquée (clavier, desktop) : offsetHeight = 0, donc padding nul.
  useEffect(() => {
    const root = document.documentElement;
    const el = barRef.current;
    if (!el) {
      root.style.setProperty(OFFSET_VAR, '0px');
      return;
    }
    const update = (): void => root.style.setProperty(OFFSET_VAR, `${el.offsetHeight}px`);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => {
      observer.disconnect();
      root.style.removeProperty(OFFSET_VAR);
    };
  }, [keyboardOpen]);

  if (keyboardOpen) return null;

  return (
    <div
      ref={barRef}
      data-section="sticky_bar"
      className="fixed inset-x-0 bottom-0 z-40 border-t border-line bg-surface/95 px-4 pt-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))] backdrop-blur-md md:hidden"
    >
      <div className="flex gap-3">
        <a
          href={`tel:${BUSINESS.phoneTel}`}
          onClick={trackClickPhone}
          className={`${BUTTON_CLASS} bg-accent-strong text-white hover:bg-accent`}
        >
          <Phone size={18} aria-hidden />
          Appeler
        </a>
        <a
          href={BUSINESS.whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={trackClickWhatsapp}
          className={`${BUTTON_CLASS} border border-line-strong bg-surface-2 text-fg hover:border-accent`}
        >
          <MessageCircle size={18} aria-hidden className="text-accent-soft" />
          WhatsApp
        </a>
      </div>
    </div>
  );
};

export default StickyContactBar;
